import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { upperFirst } from 'lodash';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { CircleCheck, CircleX, Download, Eye, RefreshCw, Send, SquarePen } from 'lucide-react';

import Card from '@dashboard/components/Card';
import ConfirmDialog from '@dashboard/components/ConfirmDialog';
import Dash from '@dashboard/components/Dash';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import NavTabs from '@dashboard/components/NavTabs';
import Link from '@dashboard/components/Link';
import StatusBadge from '@dashboard/components/StatusBadge';
import EmailCustomerDialog from '@dashboard/components/EmailCustomerDialog';
import { DataGrid } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { getEmailLogColumns } from '@dashboard/components/EmailLogColumns';
import TicketCode from '@dashboard/components/TicketCode';
import {
  Action,
  ActionGroup,
  EditGuestDialog,
  OrderActionsColumn,
  OrderPageHeader,
  OrderPageSkeleton,
  OrderTableCard,
} from '@dashboard/components/OrderPage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { formatDateTime } from '@dashboard/lib/format-date';
import { usePageRecordHref } from '@dashboard/lib/detailLinks';
import { attendeeKey, chargeEmailLogsKey, chargeKey } from '@dashboard/lib/queryKeys';
import {
  CancelChargeFormDialog,
  cancelOffReason,
} from '@dashboard/components/OrderPage/CancelChargeDialog';
import { fetcher } from '@dashboard/services/request';
import useBookingGuestActions from '../PaymentsBookingsDetail/useBookingGuestActions';
import { DETAILS_BADGES, answerRows } from './attendeeRows';

const AttendeeDetails = ({ attendee }) => {
  const { ticket, guests, booking, customer } = attendee;
  const recordHref = usePageRecordHref();
  const row = guests?.rows?.find(r => r.position === attendee.position);
  const answers = guests && row ? answerRows(guests.questions, row) : [];
  return (
    <>
      <OrderTableCard
        title="Ticket"
        rows={[
          { label: 'Ticket type', value: ticket.ticketTypeName },
          {
            label: 'Ticket code',
            value: <TicketCode code={ticket.ticketShortId} copy={false} />,
            copy: ticket.ticketShortId,
          },
          {
            label: 'Checked in',
            value:
              ticket.status === 'CANCELED' ? (
                <StatusBadge status="hidden" label="Void" />
              ) : ticket.latestCheckIn === 'CHECKEDIN' ? (
                <StatusBadge status="paid" label="Checked in" />
              ) : (
                <StatusBadge status="hidden" label="Pending" />
              ),
            copy: null,
          },
        ]}
      />
      {attendee.collectsGuests ? (
        <OrderTableCard
          title="Answers"
          rows={answers.length ? answers : [{ label: 'No details yet', value: '', copy: null }]}
        />
      ) : null}
      {booking ? (
        <OrderTableCard
          title="Booked by"
          rows={[
            {
              label: 'Customer name',
              value: customer ? (
                <Link to={recordHref('customers', customer._id)}>{customer.name || <Dash />}</Link>
              ) : (
                <Dash />
              ),
              copy: customer?.name || null,
            },
            {
              label: 'Customer email',
              value: customer?.email || <Dash />,
              copy: customer?.email || null,
            },
            {
              label: 'Booking',
              value: (
                <span>
                  <Link to={recordHref('bookings', booking._id, attendee.event._id)}>
                    {booking.orderId ? `#${booking.orderId}` : 'View booking'}
                  </Link>
                  <span className="text-gray-500 dark:text-muted-foreground">
                    {' '}
                    · {booking.ticketCount} {booking.ticketCount === 1 ? 'ticket' : 'tickets'}
                  </span>
                </span>
              ),
              copy: null,
            },
          ]}
        />
      ) : null}
    </>
  );
};

const TabCard = ({ title, children }) => (
  <div className="w-full px-4 pt-2">
    <Card className="mx-auto !px-0 !py-2">
      <CardHeader className="mb-4">
        <CardHeaderTitle>{title}</CardHeaderTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  </div>
);

const checkInColumns = [
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) =>
      row.original.status === 'CHECKEDIN' ? (
        <StatusBadge status="paid" label="Checked in" />
      ) : (
        <StatusBadge status="hidden" label="Checked out" />
      ),
  },
  { id: 'method', header: 'Method', cell: ({ row }) => upperFirst(row.original.method ?? '') },
  { id: 'time', header: 'Time', cell: ({ row }) => formatDateTime(row.original.checkedInAt) },
];

const AttendeeCheckIns = ({ attendee }) => (
  <TabCard title="Check-ins">
    <DataGrid
      columns={checkInColumns}
      data={[...(attendee.ticket.checkIns ?? [])].reverse()}
      emptyState={
        <EmptyState>
          <EmptyState.Subtitle>No check-ins for this attendee.</EmptyState.Subtitle>
        </EmptyState>
      }
    />
  </TabCard>
);

const AttendeeEmails = ({ attendee }) => {
  const { sellerId, token } = useAuth();
  const bookingId = attendee.booking?._id;
  const { data, isLoading } = useQuery({
    queryKey: chargeEmailLogsKey(bookingId),
    queryFn: () => fetcher(`/sellers/${sellerId}/email-logs?chargeId=${bookingId}`, token),
    enabled: Boolean(bookingId),
  });
  const email = attendee.email?.toLowerCase();
  const logs = useMemo(
    () =>
      (data ?? []).filter(log =>
        [log.recipients].flat().some(recipient => recipient?.toLowerCase() === email),
      ),
    [data, email],
  );
  const columns = useMemo(() => getEmailLogColumns(), []);
  return (
    <TabCard title="Emails">
      <DataGrid
        columns={columns}
        data={logs}
        isLoading={isLoading}
        emptyState={
          <EmptyState>
            <EmptyState.Subtitle>No emails found for this attendee.</EmptyState.Subtitle>
          </EmptyState>
        }
      />
    </TabCard>
  );
};

const AttendeeActions = ({ attendee }) => {
  const { sellerId, token } = useAuth();
  const recordHref = usePageRecordHref();
  const queryClient = useQueryClient();
  // Add details on a booking's Attendees tab opens the page with the details dialog open.
  const { state: openedWith } = useLocation();
  const [editing, setEditing] = useState(Boolean(openedWith?.edit));
  const bookingId = attendee.booking?._id;
  const { saveGuest, askToReview, reviewDialog, isReviewingGuest } =
    useBookingGuestActions(bookingId);
  const [confirmingResend, setConfirmingResend] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const canceled = attendee.ticket.status === 'CANCELED';
  const { data: booking } = useQuery({
    queryKey: chargeKey(bookingId),
    queryFn: () => fetcher(`/sellers/${sellerId}/charges/${bookingId}`, token),
    enabled: Boolean(bookingId),
  });
  const cancelOff =
    (canceled && 'This ticket has been canceled') ||
    (!booking && 'No booking for this ticket') ||
    cancelOffReason(booking, 'booking');
  const row = attendee.guests?.rows?.find(r => r.position === attendee.position);
  const base = `/sellers/${sellerId}/attendees/${attendee.ticket._id}`;

  const { mutate: resendTicket, isPending: isResending } = useMutation({
    mutationFn: () => fetcher(`${base}/resend-ticket`, token, { method: 'POST' }),
    onSuccess: ({ sentTo, sentToSender }) => {
      queryClient.invalidateQueries({ queryKey: chargeEmailLogsKey(bookingId) });
      toast({
        title: 'Ticket sent',
        description: sentToSender ? `Test booking: sent to you (${sentTo}).` : `Sent to ${sentTo}.`,
      });
    },
    onError: error =>
      toast({ title: "Couldn't send the ticket", description: error?.message, type: 'error' }),
  });
  const { mutate: downloadTicket, isPending: isDownloading } = useMutation({
    mutationFn: () => fetcher(`${base}/ticket-pdf`, token),
    onSuccess: ({ fileName, pdf }) => {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf}`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: () => toast({ title: 'Unable to download the ticket', type: 'error' }),
  });

  const noEmail = 'This attendee has no email address yet';
  return (
    <OrderActionsColumn>
      <ActionGroup title="Attendee">
        <Action
          icon={SquarePen}
          label={row?.added ? 'Edit details' : 'Add details'}
          onClick={row && !canceled ? () => setEditing(true) : undefined}
          off={!row || canceled}
          title={
            canceled
              ? 'This ticket has been canceled'
              : "This event doesn't collect attendee details"
          }
        />
        {row?.flagged ? (
          <Action
            icon={CircleCheck}
            label={row.reviewedAt ? 'Undo review' : 'Mark as reviewed'}
            onClick={() => askToReview(row, !row.reviewedAt)}
            loading={isReviewingGuest}
          />
        ) : null}
        {attendee.email ? (
          <EmailCustomerDialog
            label="Email attendee"
            customerEmail={attendee.email}
            livemode={attendee.booking?.livemode !== false}
            objectLabel="bookings"
            endpoint={`${base}/send-email`}
            queryKey={chargeEmailLogsKey(bookingId)}
            emailsPath={`/attendees/${attendee.ticket._id}/emails`}
          />
        ) : (
          <Action icon={Send} label="Email attendee" off title={noEmail} />
        )}
        <Action
          icon={RefreshCw}
          label="Resend ticket"
          onClick={attendee.email && !canceled ? () => setConfirmingResend(true) : undefined}
          off={!attendee.email || canceled}
          title={canceled ? 'This ticket has been canceled' : noEmail}
          loading={isResending}
        />
        <Action
          icon={Download}
          label="Download ticket"
          onClick={canceled ? undefined : () => downloadTicket()}
          off={canceled}
          title="This ticket has been canceled"
          loading={isDownloading}
        />
        <Action
          icon={CircleX}
          label="Cancel ticket"
          onClick={cancelOff ? undefined : () => setCanceling(true)}
          off={Boolean(cancelOff)}
          title={cancelOff || undefined}
        />
        <Action
          icon={Eye}
          label="View as customer"
          to={
            attendee.attendeeCustomer ? recordHref('customers', attendee.attendeeCustomer._id) : '#'
          }
          off={!attendee.attendeeCustomer}
          title="No customer has this attendee's email"
        />
      </ActionGroup>
      {attendee.booking ? (
        <ActionGroup title="Booked by">
          <Action
            icon={Eye}
            label="View customer"
            to={attendee.customer ? recordHref('customers', attendee.customer._id) : '#'}
            off={!attendee.customer}
            title="No customer on this booking"
          />
          <Action
            icon={Eye}
            label="View booking"
            to={recordHref('bookings', attendee.booking._id, attendee.event._id)}
          />
        </ActionGroup>
      ) : null}
      {row && attendee.guests ? (
        <EditGuestDialog
          guests={attendee.guests}
          row={row}
          open={editing}
          onOpenChange={setEditing}
          onSave={saveGuest}
        />
      ) : null}
      {reviewDialog}
      {canceling && booking ? (
        <CancelChargeFormDialog
          payment={booking}
          noun="booking"
          open
          onOpenChange={next => {
            if (next) return;
            setCanceling(false);
            queryClient.invalidateQueries({ queryKey: attendeeKey(attendee.ticket._id) });
          }}
          ticketId={attendee.ticket._id}
        />
      ) : null}
      <ConfirmDialog
        open={confirmingResend}
        onOpenChange={setConfirmingResend}
        title={`Email this ticket to ${attendee.email}?`}
        confirmLabel="Send"
        onConfirm={() => resendTicket()}
      />
    </OrderActionsColumn>
  );
};

const AttendeeDetail = () => {
  const { sellerId, token } = useAuth();
  const {
    url,
    params: { ticketId },
  } = useRouteMatch();
  const location = useLocation();
  const {
    data: attendee,
    isLoading,
    error,
  } = useQuery({
    queryKey: attendeeKey(ticketId),
    queryFn: () => fetcher(`/sellers/${sellerId}/attendees/${ticketId}`, token),
  });

  if (isLoading) return <OrderPageSkeleton tabs={3} />;
  if (!attendee || error) {
    return (
      <Card variant="root" contentColumn>
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = [
    {
      name: 'Details',
      href: url,
      render: () => (
        <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
          <div className="order-2 lg:order-1 w-full lg:pr-8">
            <AttendeeDetails attendee={attendee} />
          </div>
          <div className="order-1 lg:order-2 w-full lg:w-72 shrink-0 px-4 pt-3 lg:pt-[52px]">
            <AttendeeActions attendee={attendee} />
          </div>
        </div>
      ),
    },
    {
      name: 'Check-ins',
      href: `${url}/check-ins`,
      render: () => <AttendeeCheckIns attendee={attendee} />,
    },
    { name: 'Emails', href: `${url}/emails`, render: () => <AttendeeEmails attendee={attendee} /> },
  ];
  const badge = DETAILS_BADGES[attendee.detailsStatus];
  const isBooker =
    Boolean(attendee.email) &&
    attendee.email.toLowerCase() === attendee.customer?.email?.toLowerCase();

  return (
    <Card variant="root" contentColumn>
      <CardHeader variant="root">
        <OrderPageHeader
          title={attendee.name || 'Not named yet'}
          badges={
            <>
              <StatusBadge
                status={attendee.ticket.status === 'CANCELED' ? 'canceled' : 'paid'}
                label={attendee.ticket.status === 'CANCELED' ? 'Canceled' : 'Valid'}
              />
              {isBooker ? <StatusBadge status="hidden" label="Customer" /> : null}
              {badge ? <StatusBadge {...badge} /> : null}
            </>
          }
          date={
            attendee.email ? (
              <div className="text-gray-900 dark:text-foreground">{attendee.email}</div>
            ) : null
          }
        />
        <NavTabs aria-label="Attendee detail">
          {tabs.map(tab => (
            <NavTabs.Item key={tab.name} to={tab.href} active={location.pathname === tab.href}>
              {tab.name}
            </NavTabs.Item>
          ))}
        </NavTabs>
      </CardHeader>

      <CardContent variant="compact">
        <Switch>
          {tabs.map(tab => (
            <Route key={tab.href} path={tab.href} render={tab.render} exact />
          ))}
        </Switch>
      </CardContent>
    </Card>
  );
};

export default AttendeeDetail;
